<template>
  <section class="generated-page">
    <div class="generated-hero">
      <div>
        <span>Arquivo</span>
        <h1>Certificados gerados</h1>
        <p>
          Consulte os certificados emitidos, baixe PDFs assinados e acompanhe os
          metadados de assinatura digital.
        </p>
      </div>

      <div class="generated-hero-card">
        <small>Assinatura</small>
        <strong>DSS/PAdES LT</strong>
        <p>Histórico com hash, DSS/VRI e certificado utilizado.</p>
      </div>
    </div>

    <div class="generated-summary">
      <article>
        <span>Total</span>
        <strong>{{ summary.total || 0 }}</strong>
      </article>

      <article>
        <span>Assinados</span>
        <strong>{{ summary.signed || 0 }}</strong>
      </article>

      <article>
        <span>Falhas</span>
        <strong>{{ summary.failed || 0 }}</strong>
      </article>

      <article>
        <span>Com DSS</span>
        <strong>{{ summary.with_dss || 0 }}</strong>
      </article>
    </div>

    <section class="generated-panel">
      <div class="generated-toolbar">
        <div class="generated-view-toggle">
          <button
            type="button"
            :class="{ active: viewMode === 'list' }"
            @click="setViewMode('list')"
          >
            Lista
          </button>

          <button
            type="button"
            :class="{ active: viewMode === 'cards' }"
            @click="setViewMode('cards')"
          >
            Cards
          </button>
        </div>

        <div class="generated-filters">
          <input
            v-model="filters.q"
            type="search"
            placeholder="Buscar por aluno, CPF, curso ou código"
            @keyup.enter="loadCertificates(1)"
          />

          <select v-model="filters.status" @change="loadCertificates(1)">
            <option value="">Todos os status</option>
            <option value="draft">Rascunho</option>
            <option value="uploaded">PDF enviado</option>
            <option value="processed">Processado</option>
            <option value="signed">Assinado</option>
            <option value="failed">Falha</option>
          </select>

          <select
            v-model="filters.signed_with_mode"
            @change="loadCertificates(1)"
          >
            <option value="">Todos os certificados</option>
            <option value="platform">Plataforma</option>
            <option value="company">Empresa</option>
            <option value="user">Usuário</option>
          </select>

          <button type="button" @click="loadCertificates(1)">Filtrar</button>
        </div>
      </div>

      <div v-if="isLoading" class="generated-empty">
        Carregando certificados...
      </div>

      <div v-else-if="!certificates.length" class="generated-empty">
        <strong>Nenhum certificado encontrado.</strong>
        <p>
          Assim que novos certificados forem emitidos, eles aparecerão aqui.
        </p>
      </div>

      <div v-else>
        <div v-if="viewMode === 'list'" class="generated-table-wrap">
          <table class="generated-table">
            <thead>
              <tr>
                <th>Certificado</th>
                <th>Aluno</th>
                <th>Treinamento</th>
                <th>Status</th>
                <th>Assinatura</th>
                <th>Emissão</th>
                <th>Ações</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="certificate in certificates" :key="certificate.uuid">
                <td>
                  <strong>{{ certificate.public_code }}</strong>
                </td>

                <td>
                  <span>{{ certificate.student_name }}</span>
                  <small>{{
                    certificate.student_document_masked ||
                    "Documento não informado"
                  }}</small>
                </td>

                <td>
                  <span>{{ certificate.training_name }}</span>
                </td>

                <td>
                  <span
                    class="generated-status-pill"
                    :class="statusClass(certificate.status)"
                  >
                    {{ statusLabel(certificate.status) }}
                  </span>
                </td>

                <td>
                  <span>{{
                    signingModeLabel(certificate.signed_with_mode)
                  }}</span>
                  <small>{{
                    certificate.digital_certificate?.name ||
                    "Certificado padrão"
                  }}</small>
                </td>

                <td>
                  <span>{{
                    formatDateTime(
                      certificate.signed_at || certificate.created_at
                    )
                  }}</span>
                </td>

                <td>
                  <div class="generated-table-actions">
                    <button
                      type="button"
                      title="Baixar PDF"
                      :disabled="!certificate.has_final_pdf"
                      @click="downloadCertificate(certificate)"
                    >
                      PDF
                    </button>

                    <button
                      type="button"
                      title="Abrir validação pública"
                      :disabled="!certificate.validation_url"
                      @click="openValidation(certificate)"
                    >
                      Validar
                    </button>

                    <button
                      type="button"
                      title="Ver detalhes"
                      class="secondary"
                      @click="openDetails(certificate)"
                    >
                      Detalhes
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="generated-list">
          <article
            v-for="certificate in certificates"
            :key="certificate.uuid"
            class="generated-item"
          >
            <div class="generated-item-main">
              <div>
                <span>{{ certificate.public_code }}</span>
                <strong>{{ certificate.student_name }}</strong>
                <p>{{ certificate.training_name }}</p>
              </div>

              <small :class="statusClass(certificate.status)">
                {{ statusLabel(certificate.status) }}
              </small>
            </div>

            <div class="generated-meta">
              <article>
                <small>Assinatura</small>
                <strong>{{
                  signingModeLabel(certificate.signed_with_mode)
                }}</strong>
              </article>

              <article>
                <small>Certificado</small>
                <strong>{{
                  certificate.digital_certificate?.name || "Certificado padrão"
                }}</strong>
              </article>

              <article>
                <small>DSS/VRI</small>
                <strong>
                  {{ certificate.signature?.has_dss ? "DSS" : "—" }}
                  /
                  {{ certificate.signature?.has_vri ? "VRI" : "—" }}
                </strong>
              </article>

              <article>
                <small>Assinado em</small>
                <strong>{{ formatDateTime(certificate.signed_at) }}</strong>
              </article>
            </div>

            <div class="generated-card-footer">
              <div class="generated-card-hash">
                <span>SHA-256</span>
                <strong>{{ shortHash(certificate.final_sha256) }}</strong>
              </div>

              <div class="generated-actions">
                <button
                  type="button"
                  :disabled="!certificate.has_final_pdf"
                  @click="downloadCertificate(certificate)"
                >
                  Baixar PDF
                </button>

                <button
                  type="button"
                  :disabled="!certificate.validation_url"
                  @click="openValidation(certificate)"
                >
                  Validação pública
                </button>

                <button
                  type="button"
                  class="secondary"
                  @click="openDetails(certificate)"
                >
                  Detalhes
                </button>
              </div>
            </div>
          </article>
        </div>
      </div>

      <div v-if="pagination.last_page > 1" class="generated-pagination">
        <button
          type="button"
          :disabled="pagination.current_page <= 1"
          @click="loadCertificates(pagination.current_page - 1)"
          class="p-1"
        >
          Anterior
        </button>

        <span>
          Página {{ pagination.current_page }} de {{ pagination.last_page }}
        </span>

        <button
          type="button"
          :disabled="pagination.current_page >= pagination.last_page"
          @click="loadCertificates(pagination.current_page + 1)"
          class="p-1"
        >
          Próxima
        </button>
      </div>
    </section>

    <div
      v-if="selectedCertificate"
      class="generated-detail-backdrop"
      :class="{ cards: viewMode === 'cards' }"
      @click.self="selectedCertificate = null"
    >
      <section
        class="generated-detail-panel"
        :class="{ modal: viewMode === 'cards' }"
      >
        <button
          type="button"
          class="generated-detail-close"
          @click="selectedCertificate = null"
        >
          ×
        </button>

        <span>Detalhes do certificado</span>
        <h2>{{ selectedCertificate.public_code }}</h2>

        <div class="generated-detail-status">
          <small :class="statusClass(selectedCertificate.status)">
            {{ statusLabel(selectedCertificate.status) }}
          </small>

          <strong>{{ selectedCertificate.student_name }}</strong>
          <p>{{ selectedCertificate.training_name }}</p>
        </div>

        <dl>
          <div>
            <dt>Aluno</dt>
            <dd>{{ selectedCertificate.student_name }}</dd>
          </div>

          <div>
            <dt>Treinamento</dt>
            <dd>{{ selectedCertificate.training_name }}</dd>
          </div>

          <div>
            <dt>Modo de assinatura</dt>
            <dd>
              {{ signingModeLabel(selectedCertificate.signed_with_mode) }}
            </dd>
          </div>

          <div>
            <dt>Certificado digital</dt>
            <dd>
              {{
                selectedCertificate.digital_certificate?.name ||
                "Certificado padrão"
              }}
            </dd>
          </div>

          <div>
            <dt>Titular do certificado</dt>
            <dd>
              {{
                extractCommonName(
                  selectedCertificate.digital_certificate?.certificate_subject
                )
              }}
            </dd>
          </div>

          <div>
            <dt>Assinado em</dt>
            <dd>{{ formatDateTime(selectedCertificate.signed_at) }}</dd>
          </div>

          <div>
            <dt>Motor</dt>
            <dd>
              {{ selectedCertificate.signature?.engine || "Não informado" }}
            </dd>
          </div>

          <div>
            <dt>Nível</dt>
            <dd>
              {{ selectedCertificate.signature?.level || "Não informado" }}
            </dd>
          </div>

          <div>
            <dt>DSS/VRI</dt>
            <dd>
              DSS:
              {{ selectedCertificate.signature?.has_dss ? "sim" : "não" }} —
              VRI: {{ selectedCertificate.signature?.has_vri ? "sim" : "não" }}
            </dd>
          </div>

          <div class="full">
            <dt>Hash final SHA-256</dt>
            <dd>{{ selectedCertificate.final_sha256 || "Não informado" }}</dd>
          </div>

          <div class="full">
            <dt>URL pública</dt>
            <dd>{{ selectedCertificate.validation_url || "Não informado" }}</dd>
          </div>
        </dl>

        <div class="generated-detail-actions">
          <button
            type="button"
            :disabled="!selectedCertificate.has_final_pdf"
            @click="downloadCertificate(selectedCertificate)"
          >
            Baixar PDF
          </button>

          <button
            type="button"
            :disabled="!selectedCertificate.validation_url"
            @click="openValidation(selectedCertificate)"
          >
            Abrir validação
          </button>
        </div>
      </section>
    </div>
  </section>
</template>

<script setup>
import { onMounted, reactive, ref } from "vue";
import {
  downloadCertificatePdf,
  getCertificate,
  listCertificates,
} from "@/services/certificateService";

const certificates = ref([]);
const summary = ref({});
const isLoading = ref(false);
const selectedCertificate = ref(null);
const viewMode = ref(
  localStorage.getItem("certificates_generated_view_mode") || "list"
);

const filters = reactive({
  q: "",
  status: "",
  signed_with_mode: "",
  per_page: 10,
});

const pagination = reactive({
  current_page: 1,
  last_page: 1,
  per_page: 10,
  total: 0,
});

onMounted(() => {
  loadCertificates();
});

function setViewMode(mode) {
  viewMode.value = mode;
  localStorage.setItem("certificates_generated_view_mode", mode);
}

async function loadCertificates(page = 1) {
  isLoading.value = true;

  try {
    const response = await listCertificates({
      ...filters,
      page,
    });

    certificates.value = response.certificates?.data || [];
    summary.value = response.summary || {};

    pagination.current_page = response.certificates?.current_page || 1;
    pagination.last_page = response.certificates?.last_page || 1;
    pagination.per_page = response.certificates?.per_page || filters.per_page;
    pagination.total = response.certificates?.total || 0;
  } catch (error) {
    alert(error.message || "Não foi possível carregar os certificados.");
  } finally {
    isLoading.value = false;
  }
}

async function downloadCertificate(certificate) {
  try {
    const { blob, filename } = await downloadCertificatePdf(certificate.uuid);

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = filename;
    link.click();

    URL.revokeObjectURL(url);
  } catch (error) {
    alert(error.message || "Não foi possível baixar o PDF.");
  }
}

function openValidation(certificate) {
  if (!certificate.validation_url) return;

  window.open(certificate.validation_url, "_blank", "noopener,noreferrer");
}

async function openDetails(certificate) {
  try {
    selectedCertificate.value = await getCertificate(certificate.uuid);
  } catch (error) {
    alert(error.message || "Não foi possível carregar os detalhes.");
  }
}

function statusLabel(status) {
  const labels = {
    draft: "Rascunho",
    uploaded: "PDF enviado",
    processed: "Processado",
    signed: "Assinado",
    failed: "Falha",
  };

  return labels[status] || status || "Não informado";
}

function statusClass(status) {
  return (
    {
      signed: "is-signed",
      failed: "is-failed",
      processed: "is-processed",
      uploaded: "is-processed",
    }[status] || "is-neutral"
  );
}

function signingModeLabel(mode) {
  const labels = {
    platform: "Plataforma",
    company: "Empresa",
    user: "Usuário",
  };

  return labels[mode] || "Não informado";
}

function formatDateTime(value) {
  if (!value) return "Não informado";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function shortHash(value) {
  if (!value) return "Não informado";

  if (value.length <= 22) return value;

  return `${value.slice(0, 12)}...${value.slice(-10)}`;
}

function extractCommonName(value) {
  if (!value) return "Não informado";

  const match = value.match(/CN=([^,]+)/);

  return match?.[1]?.trim() || value;
}
</script>

<style scoped>
.generated-page {
  max-width: 1220px;
  margin: 0 auto;
}

.generated-hero,
.generated-panel,
.generated-summary article,
.generated-item,
.generated-modal {
  border: 1px solid rgba(214, 222, 234, 0.92);
  background: radial-gradient(
      circle at top right,
      rgba(255, 148, 23, 0.11),
      transparent 22rem
    ),
    rgba(255, 255, 255, 0.9);
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08);
}

.generated-hero {
  display: flex;
  justify-content: space-between;
  gap: 1.4rem;
  padding: 1.65rem 1.85rem;
  border-radius: 30px;
}

.generated-hero span,
.generated-modal span {
  color: #f97316;
  font-size: 0.68rem;
  font-family: "XoloniumBold", sans-serif;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.generated-hero h1 {
  margin: 0.45rem 0;
  color: #0f172a;
  font-size: clamp(1.85rem, 3.2vw, 2.75rem);
  line-height: 0.98;
  letter-spacing: -0.045em;
  font-family: "XoloniumBold", sans-serif;
}

.generated-hero p,
.generated-hero-card p,
.generated-empty p {
  max-width: 760px;
  margin: 0.35rem 0 0;
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.7;
}

.generated-hero-card {
  min-width: 250px;
  align-self: flex-start;
  padding: 1rem;
  border-radius: 22px;
  background: #0f172a;
  color: #ffffff;
}

.generated-hero-card small {
  color: #94a3b8;
  font-size: 0.65rem;
  text-transform: uppercase;
}

.generated-hero-card strong {
  display: block;
  margin-top: 0.25rem;
  font-family: "XoloniumBold", sans-serif;
}

.generated-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem;
  margin-top: 1rem;
}

.generated-summary article {
  padding: 1rem;
  border-radius: 20px;
}

.generated-summary span,
.generated-meta small,
.generated-hash span {
  display: block;
  color: #64748b;
  font-size: 0.62rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.generated-summary strong {
  display: block;
  margin-top: 0.25rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 1.15rem;
}

.generated-panel {
  margin-top: 1rem;
  padding: 1.2rem;
  border-radius: 28px;
}

.generated-filters {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 170px 190px 110px;
  gap: 0.65rem;
}

.generated-filters input,
.generated-filters select {
  min-height: 46px;
  border: 1px solid #d6deea;
  border-radius: 15px;
  padding: 0 0.85rem;
  color: #111827;
  background: #ffffff;
  outline: none;
  font-size: 0.78rem;
}

.generated-filters button,
.generated-actions button,
.generated-pagination button {
  min-height: 46px;
  border: 0;
  border-radius: 15px;
  color: #ffffff;
  background: linear-gradient(135deg, #ffb020, #ff7300);
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.68rem;
  cursor: pointer;
}

.generated-list {
  display: grid;
  gap: 0.85rem;
  margin-top: 1rem;
}

.generated-item {
  padding: 1rem;
  border-radius: 22px;
}

.generated-item-main {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.generated-item-main span {
  color: #f97316;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.62rem;
  text-transform: uppercase;
}

.generated-item-main strong {
  display: block;
  margin-top: 0.25rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.95rem;
}

.generated-item-main p {
  margin: 0.2rem 0 0;
  color: #64748b;
  font-size: 0.74rem;
}

.generated-item-main small {
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.58rem;
  white-space: nowrap;
}

.is-signed {
  color: #166534;
  background: #dcfce7;
}

.is-failed {
  color: #991b1b;
  background: #fee2e2;
}

.is-processed {
  color: #92400e;
  background: #ffedd5;
}

.is-neutral {
  color: #334155;
  background: #e2e8f0;
}

.generated-meta {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.65rem;
  margin-top: 1rem;
}

.generated-meta article {
  padding: 0.78rem;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.generated-meta strong {
  display: block;
  margin-top: 0.25rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.7rem;
  line-height: 1.35;
}

.generated-hash {
  margin-top: 0.75rem;
  padding: 0.75rem;
  border-radius: 16px;
  background: #0f172a;
  color: #ffffff;
}

.generated-hash span {
  color: #94a3b8;
}

.generated-hash strong {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.72rem;
  word-break: break-all;
}

.generated-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-top: 0.85rem;
}

.generated-actions button {
  min-height: 38px;
  padding: 0 0.85rem;
}

.generated-actions button.secondary {
  color: #334155;
  background: #e2e8f0;
}

.generated-actions button:disabled,
.generated-pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.generated-empty {
  margin-top: 1rem;
  padding: 1.2rem;
  border-radius: 20px;
  color: #64748b;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
}

.generated-empty strong {
  display: block;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
}

.generated-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
}

.generated-pagination span {
  color: #64748b;
  font-size: 0.72rem;
}

.generated-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 70;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.55);
}

.generated-modal {
  position: relative;
  width: min(760px, 100%);
  max-height: 88vh;
  overflow: auto;
  padding: 1.25rem;
  border-radius: 26px;
}

.generated-modal-close {
  position: absolute;
  top: 0.9rem;
  right: 0.9rem;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 999px;
  background: #fee2e2;
  color: #991b1b;
  cursor: pointer;
}

.generated-modal h2 {
  margin: 0.35rem 0 1rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
}

.generated-modal dl {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.generated-modal dl div.full {
  grid-column: 1 / -1;
}

.generated-modal dt {
  color: #64748b;
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.generated-modal dd {
  margin: 0.25rem 0 0;
  color: #111827;
  font-size: 0.76rem;
  line-height: 1.5;
  word-break: break-word;
}

.generated-hero-card {
  min-width: 250px;
  align-self: flex-start;
  padding: 1rem;
  border-radius: 22px;
  color: #111827;
  background: radial-gradient(
      circle at top right,
      rgba(249, 115, 22, 0.14),
      transparent 14rem
    ),
    #ffffff;
  border: 1px solid rgba(214, 222, 234, 0.95);
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.06);
}

.generated-hero-card small {
  color: #f97316;
  font-size: 0.65rem;
  text-transform: uppercase;
  font-family: "XoloniumBold", sans-serif;
}

.generated-hero-card strong {
  display: block;
  margin-top: 0.25rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
}

.generated-toolbar {
  display: grid;
  grid-template-columns: 190px minmax(0, 1fr);
  gap: 0.75rem;
  align-items: center;
}

.generated-view-toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 46px;
  padding: 0.25rem;
  border-radius: 16px;
  background: #eef2f7;
  border: 1px solid #d6deea;
}

.generated-view-toggle button {
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #64748b;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.68rem;
  cursor: pointer;
}

.generated-view-toggle button.active {
  color: #111827;
  background: #ffffff;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.generated-filters {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 170px 190px 110px;
  gap: 0.65rem;
}

.generated-filters button,
.generated-actions button,
.generated-table-actions button,
.generated-detail-actions button,
.generated-pagination button {
  min-height: 42px;
  border: 0;
  border-radius: 14px;
  color: #111827;
  background: linear-gradient(135deg, #ffb020, #ff7300);
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.66rem;
  cursor: pointer;
  box-shadow: 0 12px 24px rgba(255, 115, 0, 0.16);
}

.generated-actions button.secondary,
.generated-table-actions button.secondary {
  color: #334155;
  background: #e2e8f0;
  box-shadow: none;
}

.generated-actions button:disabled,
.generated-table-actions button:disabled,
.generated-detail-actions button:disabled,
.generated-pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.generated-table-wrap {
  margin-top: 1rem;
  overflow: hidden;
  border: 1px solid #dbe3ef;
  border-radius: 22px;
  background: #ffffff;
}

.generated-table {
  width: 100%;
  border-collapse: collapse;
}

.generated-table thead {
  background: #f8fafc;
}

.generated-table th {
  padding: 0.85rem 0.95rem;
  color: #64748b;
  font-size: 0.62rem;
  font-family: "XoloniumBold", sans-serif;
  letter-spacing: 0.08em;
  text-align: left;
  text-transform: uppercase;
  border-bottom: 1px solid #e2e8f0;
}

.generated-table td {
  padding: 0.9rem 0.95rem;
  vertical-align: middle;
  border-bottom: 1px solid #eef2f7;
}

.generated-table tr:last-child td {
  border-bottom: 0;
}

.generated-table td strong {
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.72rem;
}

.generated-table td span {
  display: block;
  color: #111827;
  font-size: 0.72rem;
  line-height: 1.35;
}

.generated-table td small {
  display: block;
  margin-top: 0.2rem;
  color: #64748b;
  font-size: 0.62rem;
  line-height: 1.35;
}

.generated-status-pill {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  padding: 0 0.65rem;
  border-radius: 999px;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.58rem !important;
  white-space: nowrap;
}

.generated-table-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.generated-table-actions button {
  min-height: 32px;
  padding: 0 0.6rem;
  font-size: 0.58rem;
  box-shadow: none;
}

.generated-card-footer {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
  margin-top: 0.85rem;
}

.generated-card-hash {
  padding: 0.75rem;
  border-radius: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.generated-card-hash span {
  display: block;
  color: #64748b;
  font-size: 0.62rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.generated-card-hash strong {
  display: block;
  margin-top: 0.25rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.72rem;
  word-break: break-all;
}

.generated-hash {
  background: #f8fafc;
  color: #111827;
  border: 1px solid #e2e8f0;
}

.generated-hash span {
  color: #64748b;
}

.generated-detail-backdrop {
  position: fixed;
  inset: 0;
  z-index: 70;
  display: flex;
  justify-content: flex-end;
  background: rgba(15, 23, 42, 0.35);
}

.generated-detail-backdrop.cards {
  justify-content: center;
  align-items: center;
  padding: 1rem;
}

.generated-detail-panel {
  position: relative;
  width: min(520px, 100%);
  height: 100%;
  overflow: auto;
  padding: 1.25rem;
  background: radial-gradient(
      circle at top right,
      rgba(249, 115, 22, 0.12),
      transparent 18rem
    ),
    #ffffff;
  border-left: 1px solid #dbe3ef;
  box-shadow: -24px 0 60px rgba(15, 23, 42, 0.18);
}

.generated-detail-panel.modal {
  width: min(760px, 100%);
  height: auto;
  max-height: 88vh;
  border-radius: 26px;
  border: 1px solid #dbe3ef;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.22);
}

.generated-detail-close {
  position: absolute;
  top: 0.9rem;
  right: 0.9rem;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 999px;
  background: #fee2e2;
  color: #991b1b;
  cursor: pointer;
}

.generated-detail-panel > span {
  color: #f97316;
  font-size: 0.68rem;
  font-family: "XoloniumBold", sans-serif;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.generated-detail-panel h2 {
  margin: 0.35rem 0 1rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
}

.generated-detail-status {
  padding: 1rem;
  border-radius: 20px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  margin-bottom: 1rem;
}

.generated-detail-status small {
  display: inline-flex;
  padding: 0.34rem 0.62rem;
  border-radius: 999px;
  font-family: "XoloniumBold", sans-serif;
  font-size: 0.58rem;
}

.generated-detail-status strong {
  display: block;
  margin-top: 0.7rem;
  color: #111827;
  font-family: "XoloniumBold", sans-serif;
}

.generated-detail-status p {
  margin: 0.25rem 0 0;
  color: #64748b;
  font-size: 0.72rem;
}

.generated-detail-panel dl {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.generated-detail-panel dl div {
  padding: 0.78rem;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
}

.generated-detail-panel dl div.full {
  grid-column: 1 / -1;
}

.generated-detail-panel dt {
  color: #64748b;
  font-size: 0.62rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.generated-detail-panel dd {
  margin: 0.25rem 0 0;
  color: #111827;
  font-size: 0.76rem;
  line-height: 1.5;
  word-break: break-word;
}

.generated-detail-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-top: 1rem;
}

.generated-detail-actions button {
  padding: 0 0.9rem;
}

@media (max-width: 1100px) {
  .generated-toolbar {
    grid-template-columns: 1fr;
  }

  .generated-view-toggle {
    max-width: 240px;
  }

  .generated-table-wrap {
    overflow-x: auto;
  }

  .generated-table {
    min-width: 980px;
  }
}

@media (max-width: 900px) {
  .generated-hero {
    flex-direction: column;
  }

  .generated-summary,
  .generated-meta,
  .generated-filters {
    grid-template-columns: 1fr;
  }

  .generated-modal dl {
    grid-template-columns: 1fr;
  }

  .generated-card-footer {
    grid-template-columns: 1fr;
  }

  .generated-detail-panel,
  .generated-detail-panel.modal {
    width: 100%;
    height: 100%;
    max-height: none;
    border-radius: 0;
  }

  .generated-detail-panel dl {
    grid-template-columns: 1fr;
  }
}
</style>
